import { reactive } from "vue";
import { usePlugins } from "@/stores/helpers/usePlugins";
import { downloadFile, type ExportFileEntry } from "@/lib/exports/downloadFile";

type Dispatch = {
  data?: { task_id?: string; files?: ExportFileEntry[] } | undefined;
  response?: { ok: boolean };
};

type WebSocketPayload = {
  id?: string;
  status?: string;
  message?: string;
  data?: { files?: ExportFileEntry[] } | undefined;
};

type UseExportsConfig<K extends string> = {
  /** WebSocket channel the export tasks are published on. */
  channel: string;
  /** The set of export types this feature supports (e.g. "topline"). */
  keys: readonly K[];
  /**
   * Fetch the bytes for a finished export file. Feature-specific until a generic
   * download route exists. The provided `signal` lets the fetch be aborted when
   * `cancel()` is called (e.g. on page reset); features that don't need
   * cancellation can ignore it.
   */
  fetchFile: (
    file: ExportFileEntry,
    signal: AbortSignal,
  ) => Promise<ArrayBuffer | undefined>;
  /** Human-readable error message for a given export key. */
  errorMessage?: (key: K) => string;
  /**
   * Optional sink for errors. Features that surface export failures through a
   * shared error ref (rather than the per-key `errors` record) can route them
   * here. Always called in addition to updating the `errors` record.
   */
  onError?: (key: K, message: string) => void;
};

function emptyRecord<K extends string, V>(keys: readonly K[], value: V) {
  return keys.reduce(
    (acc, key) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<K, V>,
  );
}

/**
 * Shared export workflow: tracks per-export-type loading/error/task state,
 * kicks off an export request + websocket subscription, and (on the matching
 * websocket message) downloads every file the task produced.
 *
 * Stores instantiate this once and delegate their websocket channel's export
 * branch to `handleMessage`, replacing the per-feature copy-paste.
 */
export function useExports<K extends string>(config: UseExportsConfig<K>) {
  const { $ws } = usePlugins();

  // `reactive` widens generic records into a mapped type that a generic key
  // can't index, so cast back to a plain record for ergonomic access.
  const loading = reactive(
    emptyRecord<K, boolean>(config.keys, false),
  ) as Record<K, boolean>;
  const errors = reactive(
    emptyRecord<K, string | undefined>(config.keys, undefined),
  ) as Record<K, string | undefined>;
  const taskIds = reactive(
    emptyRecord<K, string | undefined>(config.keys, undefined),
  ) as Record<K, string | undefined>;

  // In-flight download controllers, one per key, so `cancel` can abort a
  // download that's already streaming bytes from S3.
  const downloadControllers = emptyRecord<K, AbortController | null>(
    config.keys,
    null,
  );

  const defaultMessage = (key: K) =>
    config.errorMessage?.(key) ?? `Error creating ${key} export`;

  function reset(key: K) {
    loading[key] = false;
    taskIds[key] = undefined;
  }

  function recordError(key: K, message: string) {
    errors[key] = message;
    config.onError?.(key, message);
  }

  function setError(key: K, message?: string) {
    recordError(key, message ?? defaultMessage(key));
    reset(key);
  }

  function clearErrors() {
    config.keys.forEach((key) => {
      errors[key] = undefined;
    });
  }

  /**
   * Fetch + trigger a browser download for each file an export produced. Used by
   * both the async (websocket) and sync (immediate response) paths. Aborts
   * silently when cancelled, and records an error on any other failure.
   */
  async function downloadFiles(key: K, files: ExportFileEntry[]) {
    const controller = new AbortController();
    downloadControllers[key] = controller;
    let currentFilename = "";
    try {
      for (const file of files) {
        currentFilename = file.filename;
        const data = await config.fetchFile(file, controller.signal);
        if (controller.signal.aborted) return;
        if (!data) throw new Error(`No data returned for ${file.filename}`);
        downloadFile(file.filename, data, file.mimetype);
      }
    } catch {
      if (controller.signal.aborted) return;
      recordError(key, `Failed to download ${currentFilename}`);
    } finally {
      if (downloadControllers[key] === controller) {
        downloadControllers[key] = null;
      }
      // A cancel() already reset loading/taskId; don't clobber a fresh request.
      if (!controller.signal.aborted) reset(key);
    }
  }

  /**
   * Run an export: flip loading, perform the POST, then either subscribe for the
   * async completion message (task id) or download immediately (sync `files`).
   * Pass a thunk that performs the request and resolves with `{ data, response }`.
   * Resolves `true` when the export was kicked off (or sync-downloaded) without
   * recording an error; `false` when the request/download failed. Callers that
   * log analytics on download should check this so failed attempts are not
   * counted as successes — errors are handled internally and do not reject.
   */
  async function startExport(
    key: K,
    request: () => Promise<Dispatch>,
  ): Promise<boolean> {
    errors[key] = undefined;
    loading[key] = true;
    try {
      const result = await request();
      const taskId = result?.data?.task_id;
      const files = result?.data?.files;
      if (taskId) {
        taskIds[key] = taskId;
        $ws?.subscribe({ channel: config.channel, payload: { id: taskId } });
        return true;
      } else if (files?.length) {
        await downloadFiles(key, files);
        return !errors[key];
      } else {
        // Neither an async task nor sync files came back: treat as a failure.
        setError(key);
        return false;
      }
    } catch {
      setError(key);
      return false;
    }
  }

  /**
   * Handle a websocket payload. Returns true if the payload belonged to one of
   * this feature's exports (so the channel knows it was consumed).
   */
  async function handleMessage(payload: WebSocketPayload): Promise<boolean> {
    const key = config.keys.find(
      (k) => taskIds[k] && taskIds[k] === payload.id,
    );
    if (!key) return false;

    if (payload.status === "error") {
      setError(key, payload.message);
      return true;
    }

    const files = payload.data?.files;
    if (!files?.length) {
      // A "successful" completion that carries no files means the export
      // produced nothing to download. Surface it as an error instead of
      // silently clearing loading with no download and no error, which looks
      // to the user like the export just stopped.
      setError(key);
      return true;
    }

    await downloadFiles(key, files);
    return true;
  }

  /**
   * Abort any in-flight download(s) and clear loading/task state without
   * recording an error. Pass a key to cancel one export, or omit to cancel all.
   */
  function cancel(key?: K) {
    const keys = key ? [key] : config.keys;
    keys.forEach((k) => {
      downloadControllers[k]?.abort();
      downloadControllers[k] = null;
      reset(k);
    });
  }

  return {
    loading,
    errors,
    taskIds,
    startExport,
    handleMessage,
    cancel,
    setError,
    clearErrors,
    reset,
  };
}
